import { useState } from 'react';
import type { ActionItemView } from '@retro/shared';
import { ACTION_STATUS_LABELS } from '@retro/shared';
import { useRetroStore } from '../../lib/retroStore.js';
import { Avatar, Modal } from '../ui.js';

/**
 * Unfinished actions from previous retros.
 *
 * Shown at the very start of a session because following up on last time's
 * commitments is the part teams most often skip.
 */
export function CarriedOverBanner({ actions, canEdit }: { actions: ActionItemView[]; canEdit: boolean }) {
  const send = useRetroStore((state) => state.send);
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  return (
    <>
      <div className="notice-warn mb-2 flex items-center gap-3">
        <span aria-hidden>📌</span>
        <p className="min-w-0 flex-1">
          <span className="font-medium">{actions.length}</span> action
          {actions.length === 1 ? '' : 's'} from earlier retros still open.
        </p>
        <button type="button" className="btn !py-1 text-sm" onClick={() => setOpen(true)}>
          Review
        </button>
        <button
          type="button"
          className="rounded-full p-1 text-ink/50 hover:bg-black/5"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss"
        >
          ✕
        </button>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Open actions from earlier retros">
        <p className="mb-3 text-sm text-ink/60">
          {canEdit
            ? 'Tick anything the team has finished. This keeps the board honest before you add more.'
            : 'Your facilitator can tick off anything the team has finished.'}
        </p>
        <ul className="space-y-2">
          {actions.map((action) => (
            <li key={action.id} className="rounded-xl border border-black/10 p-3">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={action.status === 'DONE'}
                  disabled={!canEdit}
                  onChange={(event) =>
                    void send('action:setStatus', {
                      actionItemId: action.id,
                      status: event.target.checked ? 'DONE' : 'OPEN',
                    })
                  }
                  aria-label={`Mark “${action.title}” as done`}
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{action.title}</p>
                  {action.description ? (
                    <p className="mt-0.5 whitespace-pre-wrap text-sm text-ink/65">{action.description}</p>
                  ) : null}
                  <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-ink/55">
                    <span>{action.retroName}</span>
                    <span aria-hidden>·</span>
                    <span>{ACTION_STATUS_LABELS[action.status]}</span>
                    {action.dueDate ? (
                      <>
                        <span aria-hidden>·</span>
                        <span>due {new Date(action.dueDate).toLocaleDateString()}</span>
                      </>
                    ) : null}
                  </div>
                </div>
                {action.assignee ? (
                  <Avatar emoji={action.assignee.avatar} name={action.assignee.name} size="sm" />
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      </Modal>
    </>
  );
}
